import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Group, GroupColor, GroupInput, InstanceSummary, Person } from '@gm-binder/shared';
import { api } from '../api/client';
import { AppShell } from '../components/layout/AppShell';
import {
  PageHeader,
  Card,
  Button,
  Modal,
  Field,
  Input,
  Textarea,
  LoadingState,
  EmptyState,
} from '../components/ui';
import { useUiStore } from '../store/uiStore';
import { GROUP_COLORS, groupAccent, initials, sessionsLabel } from '../lib/groups';
import styles from './GroupsPage.module.css';

export function GroupsPage() {
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [runs, setRuns] = useState<InstanceSummary[]>([]);
  const [editing, setEditing] = useState<Group | 'new' | null>(null);
  const toast = useUiStore((s) => s.toast);

  const reload = async () => {
    try {
      const [grps, ppl, insts] = await Promise.all([
        api.listGroups(),
        api.listPeople(),
        api.listInstances(),
      ]);
      setGroups(grps);
      setPeople(ppl);
      setRuns(insts);
    } catch {
      setGroups([]);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const personById = useMemo(() => new Map(people.map((p) => [p.id, p] as const)), [people]);
  const sessionCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of runs) if (r.groupId) m.set(r.groupId, (m.get(r.groupId) ?? 0) + 1);
    return m;
  }, [runs]);

  const save = async (input: GroupInput) => {
    try {
      if (editing && editing !== 'new') {
        await api.updateGroup(editing.id, input);
        toast('Gruppo aggiornato', 'success');
      } else {
        await api.createGroup(input);
        toast('Gruppo creato', 'success');
      }
      setEditing(null);
      await reload();
    } catch {
      toast('Salvataggio fallito', 'danger');
    }
  };

  return (
    <AppShell>
      <PageHeader
        eyebrow="I tuoi tavoli"
        title="Gruppi"
        subtitle="Le compagnie con cui giochi. Da qui avvii le one-shot e pianifichi le serate."
        actions={
          <>
            <Link to="/people">
              <Button icon="📇">Rubrica</Button>
            </Link>
            <Button variant="primary" icon="✦" onClick={() => setEditing('new')}>
              Nuovo gruppo
            </Button>
          </>
        }
      />

      {groups == null ? (
        <LoadingState />
      ) : groups.length === 0 ? (
        <EmptyState
          icon="👥"
          title="Nessun gruppo"
          description="Crea la tua prima compagnia di giocatori: potrai riusarla per ogni nuova one-shot."
          actions={
            <Button variant="primary" onClick={() => setEditing('new')}>
              Crea gruppo
            </Button>
          }
        />
      ) : (
        <div className={styles.grid}>
          {groups.map((g) => {
            const members = g.memberIds
              .map((id) => personById.get(id))
              .filter((p): p is Person => !!p);
            const sessions = sessionCount.get(g.id) ?? 0;
            return (
              <Link
                key={g.id}
                to={`/groups/${g.id}`}
                className={styles.cardLink}
                style={{ ['--accent' as string]: groupAccent(g.color) }}
              >
                <Card interactive className={styles.card}>
                  <div className={styles.cardHead}>
                    <span className={styles.seal} aria-hidden />
                    <h3 className={styles.name}>{g.name}</h3>
                  </div>

                  <div className={styles.avatars}>
                    {members.slice(0, 6).map((p) => (
                      <span key={p.id} className={styles.avatar} title={p.name}>
                        {initials(p.name)}
                      </span>
                    ))}
                    {members.length === 0 && (
                      <span className={styles.hint}>Nessun membro ancora</span>
                    )}
                  </div>

                  <div className={styles.footer}>
                    <span>
                      {members.length} {members.length === 1 ? 'membro' : 'membri'}
                    </span>
                    <span className={styles.dot}>·</span>
                    <span>{sessions ? sessionsLabel(sessions) : 'Nessuna sessione'}</span>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {editing != null && (
        <GroupModal
          group={editing === 'new' ? null : editing}
          people={people}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </AppShell>
  );
}

const EMPTY: GroupInput = { name: '', color: 'gold', notes: '', memberIds: [] };

export function GroupModal({
  group,
  people,
  onClose,
  onSave,
}: {
  group: Group | null;
  people: Person[];
  onClose: () => void;
  onSave: (input: GroupInput) => void | Promise<void>;
}) {
  const [form, setForm] = useState<GroupInput>(
    group
      ? { name: group.name, color: group.color, notes: group.notes, memberIds: [...group.memberIds] }
      : EMPTY,
  );
  const memberIds = form.memberIds ?? [];

  const toggleMember = (id: string) =>
    setForm((f) => {
      const cur = f.memberIds ?? [];
      return {
        ...f,
        memberIds: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
      };
    });

  return (
    <Modal open onClose={onClose} title={group ? 'Modifica gruppo' : 'Nuovo gruppo'} width={520}>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.name?.trim()) return;
          void onSave(form);
        }}
      >
        <Field label="Nome del gruppo">
          <Input
            value={form.name}
            autoFocus
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Es. Il gruppo del martedì"
          />
        </Field>

        <Field label="Colore" hint="Per riconoscere il tavolo a colpo d'occhio.">
          <div className={styles.swatches}>
            {GROUP_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={[styles.swatch, form.color === c ? styles.swatchOn : '']
                  .filter(Boolean)
                  .join(' ')}
                style={{ ['--accent' as string]: groupAccent(c) }}
                aria-label={c}
                aria-pressed={form.color === c}
                onClick={() => setForm((f) => ({ ...f, color: c as GroupColor }))}
              />
            ))}
          </div>
        </Field>

        <Field
          label="Membri"
          hint={
            people.length
              ? 'Tocca per aggiungere o togliere dal gruppo.'
              : 'La rubrica è vuota: aggiungi prima delle persone.'
          }
        >
          <div className={styles.members}>
            {people.map((p) => {
              const on = memberIds.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  className={[styles.memberChip, on ? styles.memberOn : ''].filter(Boolean).join(' ')}
                  aria-pressed={on}
                  onClick={() => toggleMember(p.id)}
                >
                  <span className={styles.memberAvatar}>{initials(p.name)}</span>
                  {p.name}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Note">
          <Textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Sede abituale, orari, preferenze del gruppo…"
            rows={2}
          />
        </Field>

        <div className={styles.formActions}>
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" variant="primary" disabled={!form.name?.trim()}>
            Salva
          </Button>
        </div>
      </form>
    </Modal>
  );
}
